import { useState, useCallback } from "react";

import type { ValidationErrors } from "lib/api";

type BaseErrorKey = "base";

let valueBeforeFocus = "";

/**
 *
 * This react hook is designed similar to https://formik.org/docs/api/useFormik
 * to make it easier to migrate to Formik later, but not add another dependency now
 */
const useApiForm = <Attributes extends string, ApiReturnType>({
  apiCall,
  onSuccess,
  onError,
  initialValues,
  validate = () => ({}),
}: {
  apiCall: (values: Record<Attributes, string>) => Promise<ApiReturnType | ValidationErrors<Attributes>>;
  onSuccess: (result: ApiReturnType) => void;
  onError?: (error: unknown) => void;
  initialValues: Record<Attributes, string>;
  validate?: (values: Record<Attributes, string>) => Partial<Record<Attributes | BaseErrorKey, string>>;
}) => {
  type FormValues = Record<Attributes, string>;
  type FormErrors = Partial<Record<Attributes | BaseErrorKey, string | string[]>>;
  type FormTouched = Partial<Record<Attributes, boolean>>;

  const [values, setValues] = useState<FormValues>(initialValues);
  const [errors, setErrors] = useState<FormErrors>(validate(initialValues));
  const [touched, setTouched] = useState<FormTouched>({});
  const [sending, setSending] = useState<boolean>(false);
  const [withServerData, setWithServerData] = useState<boolean>(false);

  const onSubmit = useCallback(
    async (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      setWithServerData(false);
      setSending(true);
      setErrors({});
      const touchedAllFields = Object.fromEntries(Object.keys(values).map((key) => [key, true])) as FormTouched;
      setTouched(touchedAllFields);

      try {
        const result = await apiCall(values);
        if ("errors" in result) {
          setErrors(result.errors);
        } else {
          setErrors({});
          onSuccess(result);
        }
      } catch (error) {
        // TODO: send to Sentry?
        setErrors({ base: "Something went wrong, try again later" } as FormErrors);
        if (onError !== undefined) {
          onError(error);
        }
      } finally {
        setSending(false);
        setWithServerData(true);
      }
    },
    [values, apiCall, onError, onSuccess]
  );

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    if (e.target.value !== valueBeforeFocus) {
      e.target.reportValidity();
      setTouched({
        ...touched,
        [e.target.name]: true,
      });

      if (Object.values(values).every((value) => !value)) {
        setErrors({});
      }
    }
  };

  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    valueBeforeFocus = values[e.target.name as Attributes];
    setTouched({
      ...touched,
      [e.target.name]: false,
    });
  };

  const handleInvalid = (e: React.SyntheticEvent<HTMLInputElement>) => {
    e.preventDefault();
    const target = e.target as HTMLInputElement;
    setErrors({
      ...errors,
      [target.name]: target.validationMessage || "Invalid",
    });
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValues = {
      ...values,
      [e.target.name]: e.target.value,
    };
    const clientsideValidationErrors = validate(newValues);
    const newErrors = {
      ...errors,
      base: "",
      [e.target.name]: "",
      ...clientsideValidationErrors,
    };

    setValues(newValues);
    setErrors(newErrors);
    setWithServerData(false);
  };

  return {
    values,
    errors,
    touched,
    sending,
    handleInvalid,
    handleChange,
    handleBlur,
    handleFocus,
    onSubmit,
    withServerData,
  };
};
export default useApiForm;
